import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import * as constants from "Constants";
// import httpService from 'services/httpService.js';
import { Alert, Spinner } from "react-bootstrap";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
// import { toast } from "react-toastify";
import { FcExpired } from "react-icons/fc";
import { MdDoneAll, MdOpenInNew } from "react-icons/md";
import OnReloadFullScreenLoader from "Components/OnReloadFullScreenLoader";
import ConfirmationModal from "./Components/SubmitQuizView/ConfirmationModal";
import SubmitConfirmModal from "./Components/SubmitQuizView/SubmitConfirmModal";
import TopInfoBox from "./Components/SubmitQuizView/TopInfoBox";
import StudentFiles from "./Components/SubmitQuizView/StudentFiles";
import QuizTimeLeft from "./Components/SubmitQuizView/QuizTimeLeft";
import QuestionsNavigation from "./Components/SubmitQuizView/QuestionsNavigation";
import QuestionsList from "./Components/SubmitQuizView/QuestionsList";
import log from "loglevel";
import httpService from "services/httpService";
import { autosaveQuizRequest } from "Store/Course/Instructor/OtherLabs/Actions";

// Quiz answer changes are batched and persisted via this interval (ms).
// Keep low enough that a browser crash loses no more than ~30s of edits.
const QUIZ_AUTOSAVE_INTERVAL_MS = 30000;

const OtherActivity = ({
  otherActivityDetails,
  labAvailableTill,
  labId,
  urlFrom,
  isExam,
  handleTimeUp,
  handleLabTimeUp,
  studentData
}) => {

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const studentUserinfo = useSelector(state => state?.userInfo?.username);
  const userId = useSelector((state) => state.userInfo.userId);

  const { courseId, activityID } = params;

  const [otherActivityData, setOtherActivityData] = useState(otherActivityDetails);
  const [labAvailableTime, setLabAvailableTill] = useState(labAvailableTill ? labAvailableTill : (location.labData ? location.labData.available_till : null));

  // Sync labAvailableTill when prop changes (e.g. from WebSocket lab_settings_changed)
  useEffect(() => {
    if (labAvailableTill) {
      setLabAvailableTill(labAvailableTill);
    }
  }, [labAvailableTill]);

  const [relatedClabActivity, setRelatedClabActivity] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  // Confirmation modal state (shown before submission)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [unansweredCount, setUnansweredCount] = useState(0);

  const [autoSubmit, setAutoSub] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(true);

  const [student_name, setStudentName] = useState("");
  const [student_email, setStudentEmail] = useState("");

  const [studentFiles, setStudentFiles] = useState([]);
  const [showStudentFilesModal, setShowStudentFilesModal] = useState(false);
  const [activity_evaluated, setActivityEvaluated] = useState(false);
  const [showMarksToStudent, setShowMarksToStudent] = useState(false);
  const [revealAnswers, setRevealAnswers] = useState(false);

  const [isDataFetching, setIsDataFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(false);
  const [labExpired, setIsLabExpired] = useState(false);
  const [millisecondsLeft, setMilliSecondsLeft] = useState(0);

  useEffect(() => {
    if (otherActivityData && (isExam || labAvailableTime || millisecondsLeft > 0)) {
      setShowFullScreenLoader(false);
    }
  }, [otherActivityData, isExam, labAvailableTime, millisecondsLeft]);

  const questionRefs = useRef([]);

  // Quiz autosave: students edit answers in local state only. Every
  // QUIZ_AUTOSAVE_INTERVAL_MS we POST the full question_set to the autosave
  // endpoint, replacing the previous per-option API calls.
  const isDirtyRef = useRef(false);
  const otherActivityDataRef = useRef(otherActivityData);
  useEffect(() => {
    otherActivityDataRef.current = otherActivityData;
  }, [otherActivityData]);

  const markDirty = () => {
    isDirtyRef.current = true;
  };

  // Build the autosave payload from current local question_set state
  const buildAutosavePayload = () => {
    const data = otherActivityDataRef.current;
    if (!data?.question_set) return null;
    return data.question_set.map((q) => {
      const resp = q?.questionresponse_set || {};
      const entry = { id: q.id, question_type: q.question_type };
      if (q.question_type === "S" || q.question_type === "M") {
        entry.questionresponse_set = {
          response_options_ids: resp.response_options_ids || [],
        };
      } else {
        entry.questionresponse_set = {
          response_text: resp.response_text || "",
        };
      }
      return entry;
    });
  };

  const flushAutosave = async () => {
    if (!activityID) return false;
    const questionSet = buildAutosavePayload();
    if (!questionSet) return false;
    const result = await autosaveQuizRequest(
      activityID,
      questionSet,
      student_email,
      student_name,
    );
    if (result?.success) {
      isDirtyRef.current = false;
      return true;
    }
    return false;
  };

  // Periodic autosave timer
  useEffect(() => {
    if (!activityID) return undefined;
    const interval = setInterval(() => {
      // Skip when the quiz is locked (submitted single-attempt, expired,
      // disabled). Editing isn't possible in those states so isDirty
      // shouldn't be true anyway, but this is a defensive guard.
      if (isDisabled) return;
      if (!isDirtyRef.current) return;
      flushAutosave();
    }, QUIZ_AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activityID, isDisabled, student_email, student_name]);

  // Warn user before leaving page if they have unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return undefined;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (labExpired || isDataFetching || isSubmitting) {
      setIsDisabled(true);
    } else if (isSubmitted && !allowMultipleSubmissions) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [labExpired, isSubmitted, isSubmitting, isDataFetching, allowMultipleSubmissions]);

  // Recalculate exam timer when studentData changes (e.g. from WebSocket time_modified)
  useEffect(() => {
    if (!isExam || !studentData) return;
    let examEndMs;
    if (studentData.end_time) {
      examEndMs = new Date(studentData.end_time).getTime();
    } else if (studentData.start_time && studentData.duration) {
      examEndMs = new Date(studentData.start_time).getTime() +
        moment.duration(studentData.duration).asMilliseconds();
    }
    if (examEndMs) {
      const msLeft = examEndMs - Date.now();
      setMilliSecondsLeft(msLeft > 0 ? msLeft : 0);
    }
  }, [isExam, studentData]);

  // Recalculate millisecondsLeft when labAvailableTime changes (e.g. from WebSocket)
  // This also changes the TimerComponent key, forcing a remount with the new deadline
  useEffect(() => {
    if (!isExam && labAvailableTime) {
      const till = moment(labAvailableTime);
      const now = moment();
      const msLeft = till.diff(now);
      setMilliSecondsLeft(msLeft > 0 ? msLeft : 0);
    }
  }, [labAvailableTime, isExam]);

  useEffect(() => {
    // For exam mode, use millisecondsLeft (from time_left) to determine expiry
    if (isExam) {
      if (millisecondsLeft !== null && millisecondsLeft !== undefined) {
        setIsLabExpired(millisecondsLeft <= 0);
      }
    } else {
      // For regular labs, use labAvailableTime (from available_till)
      if (labAvailableTime) {
        let till = moment(labAvailableTime);
        let now = moment();
        if (now.isAfter(till)) {
          setIsLabExpired(true);
        } else {
          setIsLabExpired(false);
        }
      }
    }
  }, [labAvailableTime, millisecondsLeft, isExam]);

  const fetchClabActivity = async (clabActId) => {
    if (!clabActId) return;
    try {
      const response = await httpService.get(`${constants.base_url}/clab/activity/${clabActId}/`);
      if (response && response.data) {
        setRelatedClabActivity(response.data);
      } else {
        setRelatedClabActivity(null);
      }
    } catch (error) {
      console.error('Error fetching related cLab activity:', error);
      setRelatedClabActivity(null);
    }
  };

  useEffect(() => {
    const fetchActivityDetails = async (activityID) => {
      const response = await httpService.get(`${constants.base_url}/lab/activity/${activityID}?type=other`);
      const activityData = response.data?.other_activity;

      setOtherActivityData(activityData);
      setShowMarksToStudent(activityData?.show_marks_to_student === true);
      setRevealAnswers(activityData?.reveal_answers === true);
      setAllowMultipleSubmissions(activityData?.allow_multiple_submissions === true);

      if (Array.isArray(activityData?.requiredfiles_set)) {
        setStudentFiles(activityData.requiredfiles_set.map((file) => ({ ...file, isUploaded: true })));
      }
    };

    const fetchOtherActivityDetails = async () => {
      if (!otherActivityDetails.is_published) {
        navigate("/"); // Redirect to home page if not published
        return;
      }

      setOtherActivityData(otherActivityDetails);
      setActivityEvaluated(otherActivityDetails?.evaluated || false);
      setShowMarksToStudent(otherActivityDetails?.show_marks_to_student === true);
      setRevealAnswers(otherActivityDetails?.reveal_answers === true);
      setAllowMultipleSubmissions(otherActivityDetails?.allow_multiple_submissions === true);
      setLabAvailableTill(otherActivityDetails?.lab_data?.available_till || null);

      // In exam mode, compute time left from the student's individual timing
      if (isExam && studentData) {
        let examEndMs;
        if (studentData.end_time) {
          examEndMs = new Date(studentData.end_time).getTime();
        } else if (studentData.start_time && studentData.duration) {
          examEndMs = new Date(studentData.start_time).getTime() +
            moment.duration(studentData.duration).asMilliseconds();
        }
        if (examEndMs) {
          const msLeft = examEndMs - Date.now();
          setMilliSecondsLeft(msLeft > 0 ? msLeft : 0);
        } else {
          setMilliSecondsLeft(otherActivityDetails?.lab_data?.time_left || 0);
        }
      } else {
        setMilliSecondsLeft(otherActivityDetails?.lab_data?.time_left || 0);
      }
      // fetchClabActivity(otherActivityDetails?.virtualActivity);

      if (Array.isArray(otherActivityDetails?.requiredfiles_set)) {
        setStudentFiles(otherActivityDetails.requiredfiles_set.map((file) => ({ ...file, isUploaded: true })));
      }
    };

    const fetchStudentData = async (activityID, studentUserinfo) => {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/${studentUserinfo}/`;
      const response = await httpService.get(url);
      const responseData = response.data.data?.[studentUserinfo];

      if (responseData) {
        const updatedActivity = { ...otherActivityDetails };

        if (updatedActivity.question_set?.length > 0) {
          updatedActivity.question_set.forEach((question) => {
            const matchingResponse = responseData?.responses?.find((item) => item.question === question.id);
            if (matchingResponse) question.questionresponse_set = matchingResponse;
          });
          setOtherActivityData(updatedActivity);
        }
        // Check actual submission status, not just presence of saved responses
        setIsSubmitted(!!responseData.submitted);
      } else {
        setIsSubmitted(false);
      }
    };

    const fetchUserProfile = async (userId) => {
      const response = await httpService.get(`/accounts/api/profile/`);
      const profile = response.data?.[0];
      if (profile) {
        setStudentEmail(profile.email);
        setStudentName(`${profile.first_name} ${profile.last_name}`);
      }
    };

    const fetchData = async () => {
      try {
        setIsDataFetching(true);

        if (activityID) await fetchActivityDetails(activityID);
        if (otherActivityDetails) await fetchOtherActivityDetails();
        await fetchStudentData(activityID, studentUserinfo);
        await fetchUserProfile(userId);

      } catch (error) {
        log.error("Error fetching data:", error);
        if (error.response?.status === 404) {
          navigate("/");
          return;
        }
      } finally {
        setIsDataFetching(false);
      }
    };

    // const fetchData = async () => {
    //   try {
    //     setIsDataFetching(true);
    //     if (activityID) {
    //       const response = await httpService.get(constants.base_url + "/lab/activity/" + activityID + "?type=other");
    //       log.log("Activity Response Details ", response, response.data?.other_activity?.lab_data?.available_till);
    //       setOtherActivityData(response.data?.other_activity);
    //       if (Array.isArray(response.data.other_activity?.requiredfiles_set)) {
    //         setStudentFiles(response.data.other_activity?.requiredfiles_set?.map(r => ({ ...r, isUploaded: true })));
    //       }
    //     }
    //     if (otherActivityDetails) {
    //       if (!otherActivityDetails.is_published) {
    //         log.log("home page")
    //         navigate("/"); // Redirect to home page if not published
    //         return;
    //       }
    //       setOtherActivityData(otherActivityDetails);
    //       setActivityEvaluated(otherActivityDetails?.evaluated || false);
    //       if (otherActivityDetails?.lab_data?.available_till) {
    //         setLabAvailableTill(otherActivityDetails.lab_data.available_till);
    //       }
    //       if (otherActivityDetails?.lab_data?.time_left) {
    //         setMilliSecondsLeft(otherActivityDetails.lab_data.time_left);
    //       }
    //       if (Array.isArray(otherActivityDetails?.requiredfiles_set)) {
    //         setStudentFiles(otherActivityDetails?.requiredfiles_set?.map(r => ({ ...r, isUploaded: true })));
    //       }

    //       const base_other_activity_submissions_url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions`
    //       const students_data_url = `${base_other_activity_submissions_url}/${studentUserinfo}/`;

    //       const response = await httpService.get(students_data_url);
    //       const responseData = response.data.data?.[studentUserinfo];
    //       if (responseData) {
    //         const updatedOtherActivityDetails = { ...otherActivityDetails };
    //         let updated = false; // Flag to track if otherActivityDetails has been updated
    //         log.log({ updatedOtherActivityDetails, responseData });
    //         if (updatedOtherActivityDetails.question_set.length > 0) {
    //           for (let i = 0; i < updatedOtherActivityDetails?.question_set?.length; i++) {
    //             const question = updatedOtherActivityDetails.question_set[i];
    //             log.log("okkkk question ", question.id);

    //             // Find the matching response data for this question
    //             const matchingResponse = responseData?.find?.(item => item.question === question.id);

    //             if (matchingResponse) {
    //               // Replace questionresponse_set with matching response data
    //               question.questionresponse_set = matchingResponse;
    //               log.log("okkkk matches ", question.id, question);
    //               updated = true;
    //             }
    //             setIsSubmitted(true);
    //           }
    //         } else {
    //           // if ()
    //           const submittedResponse = responseData?.submitted;
    //           setIsSubmitted(submittedResponse);
    //         }

    //         // Only update otherActivityDetails if it has been modified
    //         log.log("okkkk updated ", updated);
    //         if (updated) {
    //           setOtherActivityData(updatedOtherActivityDetails);
    //           log.log("okkkk data", updatedOtherActivityDetails);
    //           log.log("okkk updated data", updatedOtherActivityDetails);
    //           setIsSubmitting(true);
    //         } else {
    //           log.log("okkkk matches", 0);
    //         }
    //       } else {
    //         setIsSubmitted(false);
    //       }
    //     }
    //     const user_resp = await httpService.get(`/accounts/api/profile/?user=${userId}`);
    //     const profiles = user_resp.data;
    //     let this_profile = profiles?.find(f => f.user === userId);
    //     setStudentEmail(this_profile?.email);
    //     setStudentName(this_profile?.first_name + " " + this_profile?.last_name);
    //     log.log("current user : ", student_email, student_name)
    //   } catch (error) {
    //     log.log(error);
    //   } finally {
    //     setIsDataFetching(false);
    //   }
    // };

    fetchData();
  }, [labId, otherActivityDetails]);

  const handleCloseStudentFilesModal = () => {
    setShowStudentFilesModal(false);
  }

  const handleOpenStudentFilesModal = () => {
    setShowStudentFilesModal(true);
  }

  // OLD CODE - Confirmation modal before submit
  // const handleConfirmSubmission = async () => {
  //   setShowModal(false);
  //   setIsSubmitting(true);

  //   const url = `${constants.base_url}/otherlabs/activity/${activityID}/student_submission/`;

  //   try {
  //     const response = await httpService.post(url, otherActivityData, {
  //       params: {
  //         stud_id: student_email,
  //         username: student_name,
  //       },
  //       headers: {
  //         "Content-Type": "multipart/form-data",
  //       },
  //     });

  //     if (response.status === 200 && response.data?.success) {
  //       setIsSubmitted(true);
  //       setShowFullScreenLoader(true);
  //       setTimeout(() => {
  //         window.location.reload();
  //       }, 1000);
  //     } else if (response.status === 200 && !response.data?.success) {
  //       displayGlobalMessage("Failed to Submit. Error occurred while saving your submission.", "error");
  //     } else {
  //       log.log("Non-success response:", response);
  //       displayGlobalMessage("Error submitting", "error");
  //     }
  //   } catch (error) {
  //     log.error("Error response:", error);
  //     displayGlobalMessage("Submission failed!", "error");
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  // NEW CODE - Direct submission with success modal
  const handleConfirmSubmission = async (isAutoSubmit = false) => {
    setIsSubmitting(true);

    // Flush any pending autosave first so QuestionResponse rows are
    // persisted before grading. The grading itself reads the request body
    // (otherActivityData) so a missed flush isn't catastrophic, but this
    // keeps server-side draft state in sync with the final submission.
    if (isDirtyRef.current) {
      await flushAutosave();
    }

    const url = `${constants.base_url}/otherlabs/activity/${activityID}/student_submission/`;

    // For auto-submit (time up): block the student immediately.
    // For exams, show "Exam Time Ended" with a spinner while the POST completes.
    // For regular labs, show "Lab Time Ended" and submit in the background.
    if (isAutoSubmit) {
      const submissionPromise = httpService.post(url, otherActivityData, {
        params: { stud_id: student_email, username: student_name },
        headers: { "Content-Type": "application/json" },
      }).then(response => {
        if (response.status === 200 && response.data?.success) {
          return "Your response has been submitted successfully.";
        }
        return "Warning: submission may not have been saved. Please contact your instructor.";
      }).catch(() => {
        throw new Error("submission failed");
      });

      if (isExam && handleTimeUp) {
        handleTimeUp(submissionPromise);
      } else if (handleLabTimeUp) {
        handleLabTimeUp();
      }
      return;
    }

    // Manual submission flow (not auto-submit)
    try {
      const response = await httpService.post(url, otherActivityData, {
        params: {
          stud_id: student_email,
          username: student_name,
        },
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data?.success) {
        setIsSubmitted(true);
        // Show success modal based on whether it was auto-submit or manual
        if (isAutoSubmit) {
          setModalMessage("Time is over. Your response has been submitted successfully.");
        } else if (allowMultipleSubmissions) {
          setModalMessage("Your response has been submitted. You can continue editing and resubmit until the deadline.");
        } else if (showMarksToStudent) {
          setModalMessage("Your Response Has Been Submitted. You can view your marks below.");
        } else {
          setModalMessage("Your Response Has Been Submitted");
        }
        setTimeout(() => { setShowModal(true); }, 500);
      } else if (response.status === 200 && !response.data?.success) {
        displayGlobalMessage("Failed to Submit. Error occurred while saving your submission.", "error");
      } else {
        log.log("Non-success response:", response);
        displayGlobalMessage("Error submitting", "error");
      }
    } catch (error) {
      log.error("Error response:", error);
      displayGlobalMessage("Submission failed!", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = async () => {
    setShowModal(false);

    if (allowMultipleSubmissions) {
      // Stay on page - quiz remains editable for resubmission
      return;
    }

    if (showMarksToStudent) {
      // Stay on page and refresh data to show marks
      try {
        const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/${studentUserinfo}/`;
        const response = await httpService.get(url);
        const responseData = response.data.data?.[studentUserinfo];

        if (responseData) {
          const updatedActivity = { ...otherActivityData };
          if (updatedActivity.question_set?.length > 0) {
            updatedActivity.question_set.forEach((question) => {
              const matchingResponse = responseData?.responses?.find((item) => item.question === question.id);
              if (matchingResponse) question.questionresponse_set = matchingResponse;
            });
            setOtherActivityData(updatedActivity);
          }
        }
      } catch (error) {
        log.error("Error refreshing data after submission:", error);
      }
    } else if (courseId && labId) {
      // Navigate back to lab activities
      navigate(`/courseware/course/${courseId}/content/labs/${labId}`);
    }
  };

  // OLD CODE - Shows confirmation modal before submit
  // const handleSubmit = () => {
  //   if (autoSubmit) {
  //     handleConfirmSubmission();
  //     return;
  //   }

  //   const missingCount = otherActivityData.required_files_num - studentFiles.length;
  //   if (missingCount > 0) {
  //     displayGlobalMessage(`Please add ${missingCount} file(s) before submitting`, "error");
  //     return;
  //   }

  //   if (!isSubmitted) {
  //     handleShowModal();
  //   } else {
  //     displayGlobalMessage("Already submitted!", "info");
  //   }
  // };

  // Confirmation modal handlers
  const handleCancelSubmit = () => setShowConfirmModal(false);
  const handleConfirmedSubmit = () => {
    setShowConfirmModal(false);
    handleConfirmSubmission(false);
  };

  const countUnanswered = () => {
    const questionSet = otherActivityData?.question_set || [];
    return questionSet.filter((q) => {
      const resp = q?.questionresponse_set || {};
      const hasText = (resp.response_text || "").trim() !== "";
      const hasOptions =
        (resp.response_options_ids || []).length > 0 ||
        (resp.response_options || []).length > 0;
      return !hasText && !hasOptions;
    }).length;
  };

  // Submit handler - shows confirmation modal for manual submissions
  const handleSubmit = (e) => {
    // Check if it's auto-submit from button data attribute or state
    const submitButton = document.getElementById("submitButton");
    const isAutoSubmitFromButton = submitButton?.getAttribute('data-auto-submit') === 'true';
    const isTimedOut = isAutoSubmitFromButton || autoSubmit;

    // Clean up the data attribute
    if (submitButton) {
      submitButton.removeAttribute('data-auto-submit');
    }

    if (isTimedOut) {
      handleConfirmSubmission(true); // Pass true for auto-submit, bypass modal
      return;
    }

    const missingCount = otherActivityData.required_files_num - studentFiles.length;
    if (missingCount > 0) {
      displayGlobalMessage(`Please add ${missingCount} file(s) before submitting`, "error");
      return;
    }

    if (!isSubmitted || allowMultipleSubmissions) {
      setUnansweredCount(countUnanswered());
      setShowConfirmModal(true); // Show confirmation modal for manual submit
    } else {
      displayGlobalMessage("Already submitted!", "info");
    }
  };

  return (
    <>
      <OnReloadFullScreenLoader customLoaderValue={showFullScreenLoader} />

      {(isDataFetching) ? <>
        <div style={{ margin: "10px" }}>
          <div className="d-flex gap-2 justify-content-center align-items-center">
            <Spinner size="sm" animation="border" />
          </div>
        </div>

      </> : <></>}

      {(labExpired) ? <>
        <Alert variant={"danger"} className="py-5" style={{ marginLeft: "10px", marginRight: "10px" }}>
          <div className="d-flex gap-2 justify-content-center align-items-center">
            <FcExpired size={28} />
            <span>This Lab has expired.</span>
          </div>
        </Alert>
      </> : <></>}

      {(isSubmitted && !isDataFetching) ? <>
        <Alert variant={allowMultipleSubmissions && !labExpired ? "success" : "info"} className="py-5" style={{ marginLeft: "10px", marginRight: "10px" }}>
          <div className="d-flex gap-2 justify-content-center align-items-center">
            <MdDoneAll size={28} />
            <span>
              {allowMultipleSubmissions && !labExpired
                ? "Submitted. You can edit and resubmit until the deadline."
                : "You have submitted."}
            </span>
          </div>
        </Alert>
      </> : <></>}

      {/* {(otherActivityDetails?.virtualActivity && !isDataFetching) ? <>
        <Alert variant={"warning"} className="py-5" style={{ marginLeft: "10px", marginRight: "10px" }}>
          <div className="d-flex gap-2 justify-content-center align-items-center">
            <p>Attempt <span style={{fontWeight: 'bold'}}>{relatedClabActivity?.name || "activity"}</span> on 
            <a href={`clab://#/course/${courseId}/lab/${labId}/activity/${relatedClabActivity?.id}`} target="_blank">
              cLab app.
              <span>
                <MdOpenInNew size={20} />
              </span>
            </a>
            </p>
          </div>
        </Alert>
      </> : <></>} */}

      <div style={{ display: "flex", marginLeft: "10px", marginRight: "10px" }}>
        <div style={{ flex: "1 1 78%", paddingRight: "20px" }}>
          <TopInfoBox
            otherActivityDetails={otherActivityDetails}
            otherActivityData={otherActivityData}
            activity_evaluated={activity_evaluated}
            showMarksToStudent={showMarksToStudent}
          />

          {/* {log.log(" evaluated ", activity_evaluated, isDisabled)} */}
          <QuestionsList
            questionRefs={questionRefs}
            activity_evaluated={(activity_evaluated && isSubmitted && showMarksToStudent) || (revealAnswers && isSubmitted && labExpired)}
            labId={labId}
            otherActivityData={otherActivityData}
            setOtherActivityData={setOtherActivityData}
            isDisabled={isDisabled}
            labAvailableTime={labAvailableTime}
            showMarksToStudent={showMarksToStudent}
            revealAnswers={revealAnswers}
            markDirty={markDirty}
          />
          {
            otherActivityData?.required_files_num > 0 ?
              <StudentFiles
                otherActivityData={otherActivityData}
                showStudentFilesModal={showStudentFilesModal}
                handleCloseStudentFilesModal={handleCloseStudentFilesModal}
                handleOpenStudentFilesModal={handleOpenStudentFilesModal}
                studentFiles={studentFiles}
                isDisabled={isDisabled}
                setStudentFiles={setStudentFiles}
                student_email={student_email}
                student_name={student_name}
              /> : <></>
          }
        </div>

        <div style={{ flex: "1 1 22%", borderLeft: "0px solid #ccc", position: "sticky", top: 0, zIndex: 1 }}>
          {(!isSubmitted || allowMultipleSubmissions) && (
            <QuizTimeLeft
              labAvailableTime={labAvailableTime}
              labExpired={labExpired}
              millisecondsLeft={millisecondsLeft}
              setAutoSub={setAutoSub}
              setIsLabExpired={setIsLabExpired}
              handleTimeUp={isExam ? handleTimeUp : handleLabTimeUp}
            />
          )}
          <QuestionsNavigation
            questionRefs={questionRefs}
            otherActivityData={otherActivityData}
          />
          {!isDisabled && <div>
            <button
              id="submitButton"
              style={{
                backgroundColor: isSubmitting ? '#fff' : 'white',
                border: isSubmitting ? 'solid 2px #ccc' : 'solid 2px #1ECD97',
                color: isSubmitting ? '#ccc' : '#1ECD97',
                width: '220px',
                height: isSubmitting ? '60px' : '60px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '30px',
                cursor: 'pointer',
                transition: 'background-color 0.4s ease',
                position: 'relative',
              }}
              onClick={handleSubmit}
              disabled={isSubmitting || isDisabled || isDataFetching}
            >
              {
                isSubmitting ? "Submitting..." : (isSubmitted && allowMultipleSubmissions ? "Resubmit" : "Submit")
              }
            </button>
          </div>}
        </div>
      </div>

      {/* Confirmation modal before submission */}
      <SubmitConfirmModal
        show={showConfirmModal}
        onCancel={handleCancelSubmit}
        onConfirm={handleConfirmedSubmit}
        allowMultipleSubmissions={allowMultipleSubmissions}
        unansweredCount={unansweredCount}
      />

      {/* Success modal after submission */}
      <ConfirmationModal show={showModal} onConfirm={handleModalClose} message={modalMessage} />
    </>
  );
}


export default OtherActivity;
